import React, { useState, useEffect } from 'react';
import {
    MessageSquare, Users, BarChart3, Shield, Zap,
    CheckCircle, ArrowRight, Globe, Clock,
    Menu, X, ClipboardList, Eye, Calendar, Briefcase,
    Smartphone, Download, HelpCircle, Upload, Loader2, Trash2 // Novos imports
} from 'lucide-react';
import api from '../services/api';

const LandingPage = ({ navigateToLogin, navigateToRegister }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [allowTrialRegistration, setAllowTrialRegistration] = useState(true);
    
    // Suporte
    const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
    const [supportForm, setSupportForm] = useState({ cpf_cnpj: '', phone: '', reason: '' });
    const [supportImages, setSupportImages] = useState([]);
    const [isSubmittingSupport, setIsSubmittingSupport] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/system/settings');
                if (res.data) setAllowTrialRegistration(res.data.allowTrialRegistration);
            } catch (err) {
                console.error("Erro ao carregar configuracoes do servidor.", err);
            }
        };
        fetchSettings();
    }, []);

    const handleRegisterClick = () => {
        if (allowTrialRegistration) {
            navigateToRegister();
        } else {
            alert("O cadastro de novas empresas não está disponível no momento.\nPor favor, contate nossa equipe.");
        }
    };

    const handleSupportSubmit = async (e) => {
        e.preventDefault();
        if (!supportForm.cpf_cnpj || !supportForm.phone || !supportForm.reason) {
            return alert("Por favor, preencha todos os campos.");
        }

        setIsSubmittingSupport(true);
        try {
            const formData = new FormData();
            formData.append('cpf_cnpj', supportForm.cpf_cnpj);
            formData.append('phone', supportForm.phone);
            formData.append('reason', supportForm.reason);
            
            supportImages.forEach(file => {
                formData.append('images', file);
            });

            await api.post('/public/support', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            alert("Sua solicitação foi enviada com sucesso! Em breve entraremos em contato.");
            setIsSupportModalOpen(false);
            setSupportForm({ cpf_cnpj: '', phone: '', reason: '' });
            setSupportImages([]);
        } catch (error) {
            alert("Erro ao enviar solicitação: " + (error.response?.data?.error || error.message));
        } finally {
            setIsSubmittingSupport(false);
        }
    };

    // --- COMPONENTES DA UI ---
    const FeatureCard = ({ icon: Icon, title, description }) => (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 flex flex-col items-start">
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
        </div>
    );

    // Componente para Seção Visual (Texto + Imagem)
    const VisualFeature = ({ title, description, imageSrc, imageAlt, reversed }) => (
        <div className={`flex flex-col lg:flex-row items-center gap-12 my-24 ${reversed ? 'lg:flex-row-reverse' : ''}`}>
            <div className="flex-1 space-y-6">
                <h3 className="text-3xl md:text-4xl font-extrabold text-gray-900">{title}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{description}</p>
                <ul className="space-y-3">
                    <li className="flex items-center text-gray-700"><CheckCircle className="w-5 h-5 text-green-500 mr-3" /> Interface intuitiva e rápida</li>
                    <li className="flex items-center text-gray-700"><CheckCircle className="w-5 h-5 text-green-500 mr-3" /> Dados atualizados em tempo real</li>
                </ul>
            </div>
            <div className="flex-1 w-full">
                <div className="relative rounded-2xl shadow-2xl border border-gray-200 overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <img
                        src={imageSrc}
                        alt={imageAlt}
                        className="w-full h-auto object-cover transform group-hover:scale-[1.02] transition-transform duration-700"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://placehold.co/800x500/f3f4f6/9ca3af?text=${imageAlt.replace(' ', '+')}`;
                        }}
                    />
                </div>
            </div>
        </div>
    );

    const PriceCheck = ({ text }) => (
        <li className="flex items-center text-gray-600 mb-3">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
            <span>{text}</span>
        </li>
    );

    return (
        <div className="min-h-screen bg-white font-sans text-gray-900">

            {/* --- HEADER / NAVBAR --- */}
            <header className="fixed w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        {/* Logo */}
                        <div className="flex items-center cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                            <img
                                src="/logo.png"
                                alt="WA-Desk"
                                className="h-10 w-auto object-contain mr-2"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                            <div className="hidden bg-blue-600 p-2 rounded-lg mr-2">
                                <MessageSquare className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-2xl font-extrabold text-blue-900 tracking-tight">WA-Desk</span>
                        </div>

                        {/* Desktop Nav */}
                        <nav className="hidden md:flex space-x-8">
                            <a href="#funcionalidades" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Funcionalidades</a>
                            <a href="#app" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">App Mobile</a>
                            <a href="#planos" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Planos</a>
                            <button onClick={() => setIsSupportModalOpen(true)} className="text-gray-600 hover:text-blue-600 font-medium transition-colors flex items-center"><HelpCircle className="w-4 h-4 mr-1"/> Suporte</button>
                        </nav>

                        {/* Desktop Buttons */}
                        <div className="hidden md:flex items-center space-x-4">
                            <button onClick={navigateToLogin} className="text-gray-900 hover:text-blue-600 font-bold transition-colors">
                                Entrar
                            </button>
                            <button onClick={handleRegisterClick} className="bg-blue-600 text-white px-6 py-2.5 rounded-full font-bold hover:bg-blue-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
                                Testar Grátis
                            </button>
                        </div>

                        {/* Mobile Menu Button */}
                        <div className="md:hidden">
                            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-gray-600">
                                {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu Dropdown */}
                {isMobileMenuOpen && (
                    <div className="md:hidden bg-white border-t border-gray-100 absolute w-full">
                        <div className="px-4 pt-2 pb-6 space-y-1 shadow-lg">
                            <a href="#funcionalidades" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-gray-700 font-medium hover:bg-blue-50 rounded-md">Funcionalidades</a>
                            <a href="#app" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-gray-700 font-medium hover:bg-blue-50 rounded-md">App Mobile</a>
                            <a href="#planos" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-gray-700 font-medium hover:bg-blue-50 rounded-md">Planos</a>
                            <button onClick={() => { setIsMobileMenuOpen(false); setIsSupportModalOpen(true); }} className="block w-full text-left px-3 py-3 text-gray-700 font-medium hover:bg-blue-50 rounded-md">Suporte</button>
                            <div className="border-t border-gray-100 my-2 pt-2">
                                <button onClick={() => { setIsMobileMenuOpen(false); navigateToLogin(); }} className="block w-full text-left px-3 py-3 text-gray-900 font-bold">Entrar</button>
                                <button onClick={() => { setIsMobileMenuOpen(false); handleRegisterClick(); }} className="block w-full mt-2 bg-blue-600 text-white text-center px-3 py-3 rounded-lg font-bold">Criar Conta</button>
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {/* --- HERO SECTION --- */}
            <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-6 text-center">

                    <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tight mb-6 leading-tight">
                        Um número de WhatsApp.<br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Sua Empresa Organizada.</span>
                    </h1>
                    <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500 mb-10">
                        Atendimento multi-atendente, agendamento de mensagens, controle de expediente e gestão de equipe. Tudo em um só lugar.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4 mb-16">
                        <button onClick={handleRegisterClick} className="px-8 py-4 bg-blue-600 text-white text-lg font-bold rounded-full hover:bg-blue-700 shadow-lg hover:shadow-blue-500/30 transition-all flex items-center justify-center">
                            Começar Agora <ArrowRight className="ml-2 w-5 h-5" />
                        </button>
                        <button className="px-8 py-4 bg-gray-100 text-gray-700 text-lg font-bold rounded-full hover:bg-gray-200 transition-all">
                            Ver Funcionalidades
                        </button>
                    </div>

                    {/* Dashboard Preview */}
                    <div className="relative mx-auto max-w-5xl rounded-2xl shadow-2xl border border-gray-200 bg-gray-50 overflow-hidden transform hover:scale-[1.01] transition-transform duration-500">
                        <div className="absolute top-0 w-full h-8 bg-gray-100 border-b flex items-center px-4 space-x-2">
                            <div className="w-3 h-3 rounded-full bg-red-400"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                            <div className="w-3 h-3 rounded-full bg-green-400"></div>
                        </div>
                        <div className="pt-8 pb-0 px-0">
                            <div className="bg-white flex items-center justify-center text-gray-300">
                                <img
                                    src="/painel1.png"
                                    alt="Dashboard WA-Desk Completo"
                                    className="w-full h-auto object-cover"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "https://placehold.co/1200x800/f3f4f6/9ca3af?text=Dashboard+Preview";
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- GRID DE FUNCIONALIDADES --- */}
            <section id="funcionalidades" className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900">Ferramentas Poderosas</h2>
                        <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
                            Centralize a comunicação e gerencie sua empresa com recursos avançados.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <FeatureCard icon={Users} title="Múltiplos Atendentes" description="Conecte vários funcionários simultaneamente usando apenas um número de WhatsApp." />
                        <FeatureCard icon={Briefcase} title="Controle de Expediente" description="Defina horários de funcionamento e ative mensagens automáticas de ausência quando fechado." />
                        <FeatureCard icon={Calendar} title="Agenda Profissional" description="Organize seus compromissos e tarefas diárias integradas diretamente ao painel de atendimento." />
                        <FeatureCard icon={Clock} title="Agendamento de Msgs" description="Programe mensagens para serem enviadas automaticamente em datas e horários específicos." />
                        <FeatureCard icon={Eye} title="Espiar Atendimentos" description="Supervisores podem visualizar conversas em tempo real e intervir ou transferir se necessário." />
                        <FeatureCard icon={ClipboardList} title="Ordens de Serviço" description="Gere, acompanhe e gerencie Ordens de Serviço (O.S.) diretamente na conversa com o cliente." />
                        <FeatureCard icon={BarChart3} title="Relatórios Detalhados" description="Métricas de tempo de resposta, volume de mensagens e produtividade da equipe." />
                        <FeatureCard icon={Shield} title="Controle de Acesso" description="Defina perfis e permissões. Restrinja o acesso a relatórios ou configurações sensíveis." />
                        <FeatureCard icon={MessageSquare} title="Chat Interno" description="Comunicação privada entre a equipe sem precisar sair da plataforma de atendimento." />
                    </div>
                </div>
            </section>

            {/* --- SEÇÃO VISUAL "POR DENTRO" --- */}
            <section id="detalhes" className="py-20 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl font-bold text-gray-900">Por dentro da plataforma</h2>
                        <p className="mt-4 text-gray-600">Veja como o WA-Desk simplifica o seu dia a dia.</p>
                    </div>

                    <VisualFeature
                        title="Atendimento Centralizado e Ágil"
                        description="Responda seus clientes em uma interface similar ao WhatsApp Web, mas com superpoderes. Transfira conversas, envie áudios, arquivos e use respostas rápidas."
                        imageSrc="/chat_preview.png"
                        imageAlt="Tela de Chat do Sistema"
                    />

                    <VisualFeature
                        title="Organização Visual e Agendamentos"
                        description="Visualize suas tarefas e agendamentos em um quadro intuitivo. Nunca mais perca um compromisso ou esqueça de enviar uma mensagem importante."
                        imageSrc="/kanban_preview.png"
                        imageAlt="Tela de Agendamentos e Organização"
                        reversed
                    />

                    <VisualFeature
                        title="Métricas para Decisões Inteligentes"
                        description="Acompanhe o desempenho da sua equipe com gráficos detalhados. Saiba o tempo médio de atendimento, volume por horário e muito mais."
                        imageSrc="/reports_preview.png"
                        imageAlt="Tela de Relatórios Gerenciais"
                    />
                </div>
            </section>

            {/* --- SEÇÃO DE MOBILIDADE (APP) --- */}
            <section id="app" className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
                        <Smartphone className="w-6 h-6 text-blue-300 mr-2" />
                        <span className="font-semibold tracking-wide">Mobilidade Total</span>
                    </div>

                    <h2 className="text-3xl md:text-5xl font-bold mb-6">Leve o WA-Desk no seu bolso</h2>
                    <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto">
                        Seu suporte não vai parar porque saiu do escritório. Utilize nosso aplicativo exclusivo para continuar atendendo de qualquer lugar.
                    </p>

                    <div className="flex flex-col sm:flex-row justify-center gap-6">
                        {/* Botão Android */}
                        <a href="https://wadesk.com.br/apk/wadesk.apk" download className="flex items-center px-6 py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all shadow-lg hover:shadow-green-500/40 transform hover:-translate-y-1 group">
                            <div className="mr-4 p-2 bg-green-800/30 rounded-lg">
                                <Download className="w-6 h-6" />
                            </div>
                            <div className="text-left">
                                <p className="text-xs text-green-100 uppercase tracking-wider font-bold">Disponível para</p>
                                <p className="text-xl font-bold leading-none">Android</p>
                            </div>
                        </a>

                        {/* Botão iOS (Em Breve) */}
                        <button className="flex items-center px-6 py-4 bg-white/10 border border-white/20 text-gray-300 rounded-xl cursor-not-allowed" title="Em breve na Apple Store">
                            <div className="mr-4 p-2 bg-white/5 rounded-lg">
                                <Smartphone className="w-6 h-6" />
                            </div>
                            <div className="text-left">
                                <p className="text-xs text-gray-400 uppercase tracking-wider font-bold">Em Breve para</p>
                                <p className="text-xl font-bold leading-none">iOS (iPhone)</p>
                            </div>
                        </button>
                    </div>
                </div>
            </section>

            {/* --- PLANOS E PREÇOS --- */}
            <section id="planos" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900">Planos que crescem com você</h2>
                        <p className="mt-4 text-gray-600">Sem fidelidade. Cancele quando quiser.</p>
                    </div>

                    <div className="flex justify-center">
                        <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-blue-100 overflow-hidden transition-transform hover:-translate-y-2 duration-300">
                            <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                                MAIS POPULAR
                            </div>
                            <div className="p-8 text-center border-b border-gray-100">
                                <h3 className="text-2xl font-bold text-gray-900">Plano Profissional</h3>
                                <div className="mt-4 flex justify-center items-baseline">
                                    <span className="text-5xl font-extrabold text-gray-900">R$ 199,90</span>
                                    <span className="ml-2 text-gray-500">/mês</span>
                                </div>
                                <p className="mt-4 text-gray-500 font-medium">Inclui 3 usuários</p>
                            </div>
                            <div className="p-8 bg-gray-50">
                                <ul className="space-y-4 mb-8">
                                    <PriceCheck text="1 Número de WhatsApp Conectado" />
                                    <PriceCheck text="3 Atendentes Inclusos" />
                                    <PriceCheck text="Usuários extras: R$ 99,90/mês" />
                                    <PriceCheck text="Controle de Expediente Automático" />
                                    <PriceCheck text="Agendamento de Mensagens" />
                                    <PriceCheck text="Agenda Profissional Integrada" />
                                    <PriceCheck text="Gerador de O.S. Integrado" />
                                    <PriceCheck text="Suporte Prioritário" />
                                </ul>
                                <button onClick={handleRegisterClick} className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 hover:shadow-lg transition-all transform hover:-translate-y-1">
                                    Contratar Agora
                                </button>
                                <p className="mt-4 text-xs text-center text-gray-400">
                                    7 dias de garantia ou seu dinheiro de volta.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- CTA FINAL --- */}
            <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                        Pronto para organizar seu atendimento?
                    </h2>
                    <p className="text-blue-100 text-lg mb-10">
                        Junte-se a centenas de empresas que já transformaram o caos do WhatsApp em resultados.
                    </p>
                    <button onClick={handleRegisterClick} className="px-10 py-4 bg-white text-blue-900 text-lg font-bold rounded-full hover:bg-gray-100 shadow-xl transition-all transform hover:scale-105">
                        Criar Conta Gratuitamente
                    </button>
                </div>
            </section>

            {/* --- FOOTER --- */}
            <footer className="bg-white border-t border-gray-200 pt-12 pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center">
                    <div className="flex items-center mb-4 md:mb-0">
                        <img
                            src="/icon.jpg"
                            alt="WA-Desk"
                            className="h-8 w-8 rounded-lg mr-2 object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className="hidden bg-blue-600 p-1.5 rounded mr-2">
                            <MessageSquare className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-lg font-bold text-gray-800">WA-Desk</span>
                    </div>

                    <div className="flex space-x-6 text-sm text-gray-500">
                        <a href="#" className="hover:text-blue-600">Termos de Uso</a>
                        <a href="#" className="hover:text-blue-600">Política de Privacidade</a>
                        <a href="#" className="hover:text-blue-600">Suporte</a>
                    </div>

                    <div className="mt-4 md:mt-0 text-sm text-gray-400">
                        © {new Date().getFullYear()} WA-Desk SaaS. Todos os direitos reservados.
                    </div>
                </div>
            </footer>

            {/* MODAL DE SUPORTE */}
            {isSupportModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center"><HelpCircle className="w-5 h-5 mr-2 text-blue-600" /> Solicitar Suporte</h2>
                            <button onClick={() => setIsSupportModalOpen(false)} className="text-gray-500 hover:text-gray-800 transition-colors p-1 rounded-md hover:bg-gray-200">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            <form onSubmit={handleSupportSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">CPF ou CNPJ</label>
                                    <input type="text" required placeholder="000.000.000-00" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={supportForm.cpf_cnpj} onChange={e => setSupportForm({ ...supportForm, cpf_cnpj: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Telefone / WhatsApp</label>
                                    <input type="text" required placeholder="(00) 00000-0000" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={supportForm.phone} onChange={e => setSupportForm({ ...supportForm, phone: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Motivo / Mensagem</label>
                                    <textarea required rows={4} placeholder="Descreva seu problema com detalhes..." className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none" value={supportForm.reason} onChange={e => setSupportForm({ ...supportForm, reason: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Anexar Imagens (Opcional)</label>
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition-colors cursor-pointer relative">
                                        <input type="file" multiple accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => {
                                            if (e.target.files) {
                                                setSupportImages(Array.from(e.target.files));
                                            }
                                        }} />
                                        <div className="flex flex-col items-center justify-center space-y-2 pointer-events-none">
                                            <Upload className="w-8 h-8 text-blue-500" />
                                            <p className="text-sm font-medium text-gray-700">Clique ou arraste imagens aqui</p>
                                        </div>
                                    </div>
                                    {supportImages.length > 0 && (
                                        <div className="mt-3 space-y-2">
                                            {supportImages.map((file, idx) => (
                                                <div key={idx} className="flex items-center justify-between bg-blue-50 p-2 rounded border border-blue-100 text-sm">
                                                    <span className="truncate flex-1 text-blue-800">{file.name}</span>
                                                    <button type="button" onClick={() => setSupportImages(supportImages.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700 p-1">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="pt-4 border-t mt-6 flex justify-end">
                                    <button type="button" onClick={() => setIsSupportModalOpen(false)} className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg font-medium mr-3 transition-colors">Cancelar</button>
                                    <button type="submit" disabled={isSubmittingSupport} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors flex items-center shadow-md disabled:opacity-70">
                                        {isSubmittingSupport ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando...</> : 'Enviar Solicitação'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LandingPage;